import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that manages dynamic price updates for add-to-cart buttons.
 * Updates the price display when quantity changes.
 */
class AddToCartButtonPriceUpdater extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    console.log('Price updater: Component initialized');
    
    // Check if we can find the price element
    const priceElement = this.querySelector('.atc-price');
    if (priceElement) {
      console.log('Price updater: Found price element with unit price:', priceElement.dataset.unitPrice);
    } else {
      console.log('Price updater: No price element found in component');
    }
    
    // Listen for quantity selector updates
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    
    // Also listen for quantity selector updates from within the same section
    const section = this.closest('.shopify-section, dialog');
    if (section) {
      section.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
      section.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
      console.log('Price updater: Added event listeners to section');
    } else {
      console.log('Price updater: No section found');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    
    const section = this.closest('.shopify-section, dialog');
    if (section) {
      section.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
      section.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }
  }

  /**
   * Handles quantity update events and updates the price display
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only update if this is not a cart line update (cart line updates are for cart page)
    if (event.detail.cartLine) return;
    
    const quantity = event.detail.quantity;
    const priceElement = this.querySelector('.atc-price');
    
    if (!priceElement) {
      console.log('Price updater: No price element found');
      return;
    }
    
    // Get the unit price from the data attribute
    const unitPriceText = priceElement.dataset.unitPrice;
    if (!unitPriceText) {
      console.log('Price updater: No unit price data attribute found');
      return;
    }
    
    const unitPrice = this.#parsePrice(unitPriceText);
    if (unitPrice === null) {
      console.log('Price updater: Could not parse unit price:', unitPriceText);
      return;
    }
    
    // Calculate total price
    const totalPrice = unitPrice * quantity;
    
    // Format the price using the same format as the original
    const formattedTotalPrice = this.#formatPrice(totalPrice);
    
    // Update the button text
    priceElement.textContent = `Add to Cart - ${formattedTotalPrice}`;
    
    console.log('Price updater: Updated price to', formattedTotalPrice, 'for quantity', quantity);
  };

  /**
   * Handles variant update events and updates the price display
   * @param {VariantUpdateEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    const priceElement = this.querySelector('.atc-price');
    if (!priceElement) {
      console.log('Price updater: No price element found for variant update');
      return;
    }
    
    // Get the new price from the variant update event
    const newPriceElement = event.detail.data.html.querySelector('.atc-price');
    if (!newPriceElement) {
      console.log('Price updater: No new price element found in variant update response');
      return;
    }
    
    // Update the data attribute and text
    const newUnitPrice = newPriceElement.dataset.unitPrice;
    if (newUnitPrice) {
      priceElement.dataset.unitPrice = newUnitPrice;
      console.log('Price updater: Updated unit price to', newUnitPrice);
    }
    
    // Get current quantity to recalculate total
    const quantitySelector = this.closest('.shopify-section, dialog')?.querySelector('quantity-selector-component input');
    const quantity = quantitySelector ? parseInt(quantitySelector.value) || 1 : 1;
    
    // Calculate new total price
    const unitPrice = this.#parsePrice(newUnitPrice || '0');
    if (unitPrice === null) {
      console.log('Price updater: Could not parse new unit price:', newUnitPrice);
      return;
    }
    
    const totalPrice = unitPrice * quantity;
    const formattedTotalPrice = this.#formatPrice(totalPrice);
    
    // Update the button text
    priceElement.textContent = `Add to Cart - ${formattedTotalPrice}`;
    
    console.log('Price updater: Updated variant price to', formattedTotalPrice, 'for quantity', quantity);
  };

  /**
   * Parses a price string to a number
   * @param {string} priceText - The price text to parse
   * @returns {number|null} The parsed price or null if parsing fails
   */
  #parsePrice(priceText) {
    // Remove currency symbols and commas, then parse as float
    const cleanPrice = priceText.replace(/[^\d.]/g, '');
    const price = parseFloat(cleanPrice);
    return isNaN(price) ? null : price;
  }

  /**
   * Formats a price number to a string
   * @param {number} price - The price to format
   * @returns {string} The formatted price
   */
  #formatPrice(price) {
    // Get currency symbol from the page or use default
    const currencySymbol = this.#getCurrencySymbol();
    
    // Format with 2 decimal places and add currency symbol
    return `${currencySymbol}${price.toFixed(2)}`;
  }

  /**
   * Gets the currency symbol from the page
   * @returns {string} The currency symbol
   */
  #getCurrencySymbol() {
    // Try to get currency symbol from cart data
    const cartCurrency = document.querySelector('[data-cart-currency]');
    if (cartCurrency) {
      return cartCurrency.dataset.cartCurrency;
    }
    
    // Try to get from cart summary
    const cartTotal = document.querySelector('[data-cart-subtotal]');
    if (cartTotal) {
      const text = cartTotal.textContent;
      const match = text.match(/^([^\d,.]*)([\d,]+\.?\d*)/);
      if (match && match[1].trim()) {
        return match[1].trim();
      }
    }
    
    // Try to get from any price element
    const priceElements = document.querySelectorAll('.price, [class*="price"]');
    for (const element of priceElements) {
      const text = element.textContent;
      const match = text.match(/^([^\d,.]*)([\d,]+\.?\d*)/);
      if (match && match[1].trim()) {
        return match[1].trim();
      }
    }
    
    // Default fallback
    return '$';
  }
}

// Register the component
if (!customElements.get('add-to-cart-button-price-updater')) {
  customElements.define('add-to-cart-button-price-updater', AddToCartButtonPriceUpdater);
} 